import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const categoryGroups = [
  { label: 'Apparel', cats: categories.slice(0, 6) },
  { label: 'More Fashion', cats: categories.slice(6, 11) },
  { label: 'Extras', cats: categories.slice(11) },
];

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileCatOpen, setIsMobileCatOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const textClass = isScrolled ? 'text-foreground-950' : 'text-background-50';
  const textHoverClass = isScrolled ? 'hover:text-foreground-950' : 'hover:text-background-100';
  const textMutedClass = isScrolled ? 'text-foreground-700' : 'text-background-100/80';
  const borderClass = isScrolled ? 'border-background-200/70' : 'border-background-50/10';
  const bgClass = isScrolled ? 'bg-background-50/95 backdrop-blur-md' : 'bg-transparent';
  const logoClass = isScrolled ? 'text-foreground-950' : 'text-background-50';
  const btnClass = isScrolled
    ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
    : 'bg-background-50 text-foreground-950 hover:bg-background-100';

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${bgClass} ${isScrolled ? `border-b ${borderClass}` : ''}`}
    >
      <div className="w-full px-4 md:px-6 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link
            to="/"
            className={`flex-shrink-0 cursor-pointer font-heading text-xl md:text-2xl font-bold tracking-tight transition-colors duration-300 ${logoClass}`}
          >
            OOPBuy
          </Link>

          <div className="hidden md:flex items-center space-x-1">
            <Link
              to="/"
              className={`px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${textMutedClass} ${textHoverClass}`}
            >
              Home
            </Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`flex items-center px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${textMutedClass} ${textHoverClass}`}
              >
                Categories
                <i className={`ri-arrow-down-s-line ml-1 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-56 bg-background-50 rounded-lg border border-background-200/80 shadow-sm py-2 z-50 animate-fade-in">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/what-is-oopbuy-spreadsheet"
              className={`px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${textMutedClass} ${textHoverClass}`}
            >
              Guide
            </Link>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={`px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${textMutedClass} ${textHoverClass}`}
            >
              Shopping Guide
            </a>
          </div>

          <div className="hidden md:flex items-center space-x-3">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={`px-5 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap cursor-pointer ${btnClass}`}
            >
              OOPBuy Spreadsheet
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${textMutedClass}`}
          >
            <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 animate-fade-in max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-3 space-y-1">
            <Link
              to="/"
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-950 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-home-4-line text-foreground-600"></i>
              </span>
              Home
            </Link>

            <Link
              to="/what-is-oopbuy-spreadsheet"
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-950 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-book-open-line text-foreground-600"></i>
              </span>
              Guide
            </Link>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-950 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-shopping-bag-3-line text-foreground-600"></i>
              </span>
              Shopping Guide
            </a>

            <div className="pt-2">
              <button
                onClick={() => setIsMobileCatOpen(!isMobileCatOpen)}
                className="flex items-center justify-between w-full px-3 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              >
                <span>Categories</span>
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line transition-transform duration-200 ${isMobileCatOpen ? 'rotate-180' : ''}`}></i>
                </span>
              </button>

              {isMobileCatOpen && (
                <div className="mt-1 ml-2 space-y-4 animate-fade-in">
                  {categoryGroups.map((group) => (
                    <div key={group.label}>
                      <p className="px-3 mb-1 text-xs font-medium text-foreground-400 uppercase tracking-wider">
                        {group.label}
                      </p>
                      <div className="space-y-0.5">
                        {group.cats.map((cat) => (
                          <a
                            key={cat.id}
                            href={cat.url}
                            target="_blank"
                            rel="noopener nofollow"
                            className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            <span className="w-5 h-5 flex items-center justify-center">
                              <i className="ri-arrow-right-up-line text-foreground-400 text-xs"></i>
                            </span>
                            {cat.name}
                          </a>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="pt-3">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="block w-full px-3 py-3 text-sm font-semibold text-center bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors cursor-pointer"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                OOPBuy Spreadsheet
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}